import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { ISO, type IsoKey } from "@/content/illustrations";
import { ISO_TOKENS, type IsoIntensity } from "@/lib/iso";

/**
 * Isometric illustration. Lazy-loaded, capped in height, and drifted slowly on
 * scroll so it feels anchored to the same floor plan as the rest of the page.
 */
export function IsoIllustration({
  name,
  alt,
  className,
  parallax = true,
}: {
  name: IsoKey;
  alt: string;
  className?: string;
  parallax?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!parallax) return;
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2) / window.innerHeight - 0.5;
      const range = ISO_TOKENS.driftArt;
      setOffset(Math.max(-range, Math.min(range, -progress * range * ISO_TOKENS.driftGain)));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [parallax]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <div
        aria-hidden
        className="iso-halo pointer-events-none absolute inset-6 -z-10 rounded-full blur-3xl"
      />
      <img
        src={ISO[name]}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{ transform: `translate3d(0, ${offset}px, 0)` }}
        className={cn(
          "iso-art mx-auto block h-auto w-full object-contain transition-transform duration-300 ease-out motion-reduce:transform-none",
          ISO_TOKENS.artMaxHeight,
        )}
      />
    </div>
  );
}

/** Isometric floor-plan backdrop. Absolute by default — give it a positioned parent. */
export function IsoGrid({
  className,
  intensity = "medium",
  drift = true,
}: {
  className?: string;
  intensity?: IsoIntensity;
  /** Slow vertical parallax of the floor lines as the section scrolls by. */
  drift?: boolean;
}) {
  const opacity = ISO_TOKENS.gridIntensity[intensity];
  const [ref, shift] = useScrollDrift<HTMLDivElement>(drift, ISO_TOKENS.driftGrid);

  return (
    <div
      ref={ref}
      aria-hidden
      style={{ "--iso-shift": `${shift}px` } as CSSProperties}
      className={cn(
        "iso-grid pointer-events-none absolute inset-0 -z-10 max-sm:opacity-25",
        opacity,
        className,
      )}
    />
  );
}

/** Media wrapper tilted onto the isometric plane, with a lime lower-left edge. */
export function IsoPlate({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("iso-plate overflow-hidden rounded-2xl border border-border bg-surface", className)}>
      {children}
    </div>
  );
}

/** Angled seam between sections — stacked platforms instead of flat rules. */
export function IsoSeam({ className }: { className?: string }) {
  return <div aria-hidden className={cn("iso-seam", className)} />;
}

/** Extruded cube marker for numbered steps and metric tiles. */
export function IsoStep({ n, className }: { n: number | string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("iso-step relative inline-grid size-11 shrink-0 place-items-center", className)}
    >
      <span className="iso-step-shadow" />
      <span className="iso-step-face" />
      <span className="relative font-display text-base font-bold text-primary">{n}</span>
    </span>
  );
}

/**
 * Shared scroll-drift helper: returns a small, clamped pixel offset based on the
 * element's position in the viewport. No-ops for reduced motion.
 */
function useScrollDrift<T extends HTMLElement>(enabled: boolean, range: number) {
  const ref = useRef<T>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
      const progress = (rect.top + rect.height / 2) / window.innerHeight - 0.5;
      setOffset(Math.max(-range, Math.min(range, -progress * range * ISO_TOKENS.driftGain)));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled, range]);

  return [ref, offset] as const;
}
